import React from 'react';
import { useNavigate } from 'react-router-dom';
import AppActionMenu from '../../../../components/AppActionMenu';

export default function LeaveMasterTable({
  records = [],
  isLoading = false,
  onArchive,
  onDelete,
  onResetFilters,
}) {
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-12 text-center shadow-sm">
        <div className="flex flex-col items-center justify-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-3 border-primary border-t-transparent" />
          <p className="text-xs font-semibold text-on-surface-variant">Loading Leave Types...</p>
        </div>
      </div>
    );
  }

  if (records.length === 0) {
    return (
      <div className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-12 text-center shadow-sm">
        <div className="flex flex-col items-center justify-center gap-3">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary">
            <span className="material-symbols-outlined text-3xl">domain_verification</span>
          </div>
          <h3 className="text-lg font-bold text-on-surface">No Leave Types Found</h3>
          <p className="text-xs text-on-surface-variant max-w-md">
            There are no leave records matching your filter criteria.
          </p>
          {onResetFilters && (
            <button
              type="button"
              onClick={onResetFilters}
              className="mt-2 rounded-xl bg-primary px-5 py-2 text-xs font-bold text-on-primary shadow-sm hover:bg-primary/90 transition cursor-pointer"
            >
              Reset Filters
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[700px] text-left text-sm">
          <thead>
            <tr className="border-b border-outline-variant/20 bg-surface-container-low/60 text-xs font-bold uppercase tracking-[0.08em] text-on-surface-variant">
              <th className="px-5 py-3.5">Name</th>
              <th className="px-5 py-3.5">Alias</th>
              <th className="px-5 py-3.5">Color</th>
              <th className="px-5 py-3.5">Status</th>
              <th className="w-20 px-5 py-3.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-outline-variant/15">
            {records.map((item) => {
              const isActive = item.status === 'Active';
              return (
                <tr
                  key={item.id}
                  onClick={() => navigate(`/payroll/leave-master/entry/${item.id}`)}
                  className="transition-colors hover:bg-surface-container-low/50 cursor-pointer group"
                >
                  {/* Name */}
                  <td className="px-5 py-3.5 font-bold text-on-surface group-hover:text-primary transition-colors">
                    {item.name}
                  </td>

                  {/* Alias */}
                  <td className="px-5 py-3.5 text-xs text-on-surface-variant font-medium">
                    {item.alias || '-'}
                  </td>

                  {/* Color */}
                  <td className="px-5 py-3.5 text-xs font-medium text-on-surface">
                    <div className="flex items-center gap-2">
                      <span
                        className="inline-block h-4 w-4 rounded-full border border-black/10 shadow-xs"
                        style={{ backgroundColor: item.color || '#3B82F6' }}
                      />
                      <span className="font-mono text-xs uppercase text-on-surface-variant">
                        {String(item.color || '').replace('#', '')}
                      </span>
                    </div>
                  </td>

                  {/* Status */}
                  <td className="px-5 py-3.5">
                    <span
                      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold tracking-wide ${
                        isActive
                          ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                          : 'bg-amber-500/10 text-amber-600 dark:text-amber-400'
                      }`}
                    >
                      <span
                        className={`h-1.5 w-1.5 rounded-full ${
                          isActive ? 'bg-emerald-500' : 'bg-amber-500'
                        }`}
                      />
                      {item.status || (isActive ? 'Active' : 'Inactive')}
                    </span>
                  </td>

                  {/* Actions */}
                  <td
                    className="px-5 py-3.5 text-right"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <AppActionMenu
                      triggerLabel=""
                      triggerIcon="more_vert"
                      showChevron={false}
                      triggerClassName="rounded-lg p-1.5 text-outline-variant transition-colors hover:bg-surface-container-low hover:text-primary bg-transparent cursor-pointer"
                      items={[
                        { key: 'edit', label: 'Edit', icon: 'edit' },
                        {
                          key: 'archive',
                          label: isActive ? 'Archive' : 'Activate',
                          icon: isActive ? 'archive' : 'unarchive',
                        },
                        { key: 'delete', label: 'Delete', icon: 'delete' },
                      ]}
                      onSelect={(key) => {
                        if (key === 'edit') {
                          navigate(`/payroll/leave-master/entry/${item.id}`);
                        } else if (key === 'archive') {
                          onArchive?.(item);
                        } else if (key === 'delete') {
                          onDelete?.(item.id, item.name);
                        }
                      }}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
